/*
 * Drawn in one style — 24×24, 2px stroke, round caps — and inlined rather than
 * pulled from a package, which would cost most of a megabyte for six glyphs.
 * The same decision both other dashboards make, and the reason their icon sets
 * look like one set despite being three files.
 */

export type IconName = "link" | "copy" | "check" | "wallet" | "users" | "logout" | "external";

const PATHS: Record<IconName, string> = {
  link: "M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7 M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7",
  copy: "M9 9h10v10a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2Z M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1",
  check: "m4 12 5 5L20 6",
  wallet: "M3 7a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z M16 12h4",
  users: "M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M22 20v-2a4 4 0 0 0-3-3.9",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9",
  external: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6 M15 3h6v6 M10 14 21 3",
};

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className ?? "size-4"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name].split(" M").map((segment, index) => (
        <path key={index} d={index === 0 ? segment : `M${segment}`} />
      ))}
    </svg>
  );
}

/**
 * The Maxlayer mark, in ink rather than in the brand gradient.
 *
 * The same geometry the platform uses everywhere — a filled top layer over two
 * fading ones, which is the whole idea in the name. Identical paths to
 * `maxlayer-clients`' `LogoMark` and to the marketing site's, because an
 * affiliate arrived here from a Maxlayer account and should recognise it.
 *
 * What changes is the fill. The platform draws it as accent-into-mint on a
 * near-black canvas, where a gradient reads as light. On white it reads as a
 * sticker — and worse, `mint` is one of the four reserved state colours, so
 * spending it on decoration here is exactly what the scarcity rule forbids.
 *
 * So: one colour, and it is `fg` rather than `#000`. The palette avoids pure
 * black deliberately — it is a harder edge than any printed page — and taking
 * the token means the mark tracks the text it sits beside rather than being a
 * hex that has to be remembered.
 *
 * The two chevrons keep their 0.7 and 0.35 opacities. In the gradient version
 * those are depth *and* hue; in monochrome they are the only thing carrying the
 * three layers, which is what makes this legible at 16px rather than a blob.
 */
export function Logo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true" className={className ?? "size-6"}>
      <path d="M16 3.5 29 11 16 18.5 3 11z" fill="var(--color-fg)" />
      <path
        d="M4.5 15.6 16 22.2l11.5-6.6"
        fill="none"
        stroke="var(--color-fg)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      <path
        d="M4.5 20.6 16 27.2l11.5-6.6"
        fill="none"
        stroke="var(--color-fg)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.35"
      />
    </svg>
  );
}
